import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Link } from '@mui/material';
import {
  AnalyticsEventName,
  Choice,
  CreatePromotionTemplateInputType,
  DiscountTarget,
  FinalChoice,
  PromotionAccessType,
  PromotionFilterKeys,
  TypeOfDiscount,
  WeeklyForecastFiltersForUpliftStats,
} from '@provider-portal/common/api/gen/CampaignDeliveryProvider';
import { useModalDialog } from '@provider-portal/common/components/ModalDialog/useModalDialog';
import { usePerformNavigate } from '@provider-portal/common/utils/navigation';
import { PromotionsAnalyticsEvents } from '@provider-portal/promotions-common/analyticsEvents';
import {
  AddPromotionDuration,
  AddPromotionOverview,
  AddPromotionStepper,
  Promotion,
  PromotionCardFC,
  PromotionDuration,
} from 'promotions/components';
import { fetchCode } from 'promotions/components/utils';

import { useSelectedProviderId } from 'hooks/useSelectedProvider';
import { selectProviderPromotionsPermissions } from 'rdx/promotions/selectors';
import { toTimestamp } from 'utils/date';

import { useCommonAppServices } from 'root/CommonAppServicesContext';
import { StatNotAvailableReason } from 'root/promotions/components/PromotionUpliftStats/PromotionUpliftStats';
import { SelectedMenuItem } from 'root/promotions/types';

import { usePromotionsApiFetcher } from '../../providers/apiClient/PromotionsApiFetchersProvider';
import { routes } from '../../routes';
import { SelectPromotionWizard } from '../SelectPromotionWizard';

interface AddPromotionStateBase {
  step: 0 | 1 | 2;
  promotion?: Promotion;
  duration?: PromotionDuration;
}

interface AddPromotionStateZero extends AddPromotionStateBase {
  step: 0;
}

interface AddPromotionStateOne extends AddPromotionStateBase {
  step: 1;
  promotion: Promotion;
}

interface AddPromotionStateTwo extends AddPromotionStateBase {
  step: 2;
  promotion: Promotion;
  duration: PromotionDuration;
}

interface WeeklyForecastFiltersForUpliftStatsWithBoltPlus
  extends WeeklyForecastFiltersForUpliftStats {
  type_of_discount?: TypeOfDiscount;
  discount_target?: DiscountTarget;
  discount_amount?: number;
  is_bolt_plus?: boolean;
}

type AddPromotionState = AddPromotionStateZero | AddPromotionStateOne | AddPromotionStateTwo;

function findError(
  messages: Record<string, string | ReactNode>,
  code: number | null,
): string | ReactNode | undefined {
  return (code && messages[code]) || undefined;
}

const LABEL = 'uplift_stats';

const typeOfDiscountKeys = [
  PromotionFilterKeys.TYPE_OF_DISCOUNT_FULL_MENU,
  PromotionFilterKeys.TYPE_OF_DISCOUNT_FREE_DELIVERY,
  PromotionFilterKeys.TYPE_OF_DISCOUNT_FREE_BASE_DELIVERY,
  PromotionFilterKeys.TYPE_OF_DISCOUNT_SELECTED_ITEMS,
  PromotionFilterKeys.TYPE_OF_DISCOUNT_DOUBLE_DEAL_v2,
];

const discountTargetingKeys = [
  PromotionFilterKeys.DISCOUNT_TARGETING_ALL_CUSTOMERS,
  PromotionFilterKeys.DISCOUNT_TARGETING_CHURNED_CUSTOMERS,
  PromotionFilterKeys.DISCOUNT_TARGETING_RETURNING_CUSTOMERS,
  PromotionFilterKeys.DISCOUNT_TARGETING_NEW_CUSTOMERS,
  PromotionFilterKeys.DISCOUNT_TARGETING_BEST_CUSTOMERS,
];

const filterKeysToTypeMapping = {
  [PromotionFilterKeys.TYPE_OF_DISCOUNT_FULL_MENU]: TypeOfDiscount.FULL_MENU,
  [PromotionFilterKeys.TYPE_OF_DISCOUNT_FREE_DELIVERY]: TypeOfDiscount.FREE_DELIVERY,
  [PromotionFilterKeys.TYPE_OF_DISCOUNT_FREE_BASE_DELIVERY]: TypeOfDiscount.FREE_BASE_DELIVERY,
  [PromotionFilterKeys.TYPE_OF_DISCOUNT_DOUBLE_DEAL_v2]: TypeOfDiscount.DOUBLE_DEAL,
  [PromotionFilterKeys.TYPE_OF_DISCOUNT_DOUBLE_DEAL]: TypeOfDiscount.DOUBLE_DEAL,
};

const filterKeysToTargetMapping = {
  [PromotionFilterKeys.DISCOUNT_TARGETING_ALL_CUSTOMERS]: DiscountTarget.ALL_CUSTOMER,
  [PromotionFilterKeys.DISCOUNT_TARGETING_RETURNING_CUSTOMERS]: DiscountTarget.RETURNING_CUSTOMER,
  [PromotionFilterKeys.DISCOUNT_TARGETING_CHURNED_CUSTOMERS]: DiscountTarget.CHURNED_CUSTOMER,
  [PromotionFilterKeys.DISCOUNT_TARGETING_NEW_CUSTOMERS]: DiscountTarget.NEW_CUSTOMER,
  [PromotionFilterKeys.DISCOUNT_TARGETING_BEST_CUSTOMERS]: DiscountTarget.BEST_CUSTOMER,
};

export const AddPromotion = () => {
  const intl = useIntl();
  const { mixpanelService } = useCommonAppServices();
  const providerId = useSelectedProviderId();
  const signInFetcher = usePromotionsApiFetcher().singIn.useFetcher();
  const forecastsFetcher = usePromotionsApiFetcher().weeklyForecasts.useFetcher();
  const analyticsFetcher = usePromotionsApiFetcher().storeAnalyticsEvent.useFetcher();
  const providerPromotionsPermissions = useSelector(selectProviderPromotionsPermissions);

  const [state, setState] = useState<AddPromotionState>({ step: 0 });
  const filterInitialState = useMemo(
    () => ({
      discount_amount: -1,
      discount_target: undefined,
      type_of_discount: undefined,
      is_bolt_plus: false,
    }),
    [],
  );
  const [selectedFilters, setSelectedFilters] =
    useState<WeeklyForecastFiltersForUpliftStatsWithBoltPlus>(filterInitialState);
  const [errorMessage, setErrorMessage] = useState<string | ReactNode>();
  const [upliftStats, setUpliftStats] = useState(undefined);
  const [shouldShowSkeletons, setShouldShowSkeletons] = useState<boolean>(true);
  const [isStatAvailable, setIsStatAvailable] = useState<boolean>(true);
  const [statNotAvailableReason, setStatNotAvailableReason] =
    useState<StatNotAvailableReason>(undefined);
  const [selectedMenuItems, setSelectedMenuItems] = useState<SelectedMenuItem[]>([]);
  const [selectedFinalChoice, setSelectedFinalChoice] = useState<FinalChoice>();
  const [selectedDiscountTargeting, setSelectedDiscountTargeting] = useState<PromotionFilterKeys>();

  const strings = useMemo(() => {
    return {
      title: intl.formatMessage({ id: 'components.AddPromotion.titleNewPromotion' }),
      publishError: {
        title: intl.formatMessage({ id: 'components.AddPromotion.publishError.title' }),
        text: intl.formatMessage({ id: 'components.AddPromotion.publishError.text' }),
        smartPromotionText: intl.formatMessage({
          id: 'components.AddPromotion.publishError.smartPromotionText',
        }),
        buttonText: intl.formatMessage({ id: 'components.AddPromotion.publishError.buttonText' }),
      },
      errorMessages: {
        5818: intl.formatMessage({ id: 'components.AddPromotion.errorMessage.code5818' }),
        5857: intl.formatMessage({ id: 'components.AddPromotion.errorMessage.code5857' }),
        5858: intl.formatMessage({ id: 'components.AddPromotion.errorMessage.code5858' }),
        5859: intl.formatMessage({ id: 'components.AddPromotion.errorMessage.code5859' }),
        5860: intl.formatMessage({ id: 'components.AddPromotion.errorMessage.code5860' }),
        66131: intl.formatMessage(
          { id: 'components.AddPromotion.errorMessage.code66131' },
          {
            link: (
              <Link
                href="https://foodpartner.bolt.eu/smart-promotions/home"
                target="_blank"
                rel="noreferrer"
              >
                {intl.formatMessage({ id: 'components.NavBar.smartPromotions' })}
              </Link>
            ),
            break: <br />,
          },
        ),
      },
      listStrings: {
        emptyListError: {
          title: intl.formatMessage({ id: 'components.AddPromotion.emptyListError.title' }),
          text: intl.formatMessage({ id: 'components.AddPromotion.emptyListError.text' }),
          buttonText: intl.formatMessage({
            id: 'components.AddPromotion.emptyListError.buttonText',
          }),
        },
      },
    };
  }, [intl]);

  const goToPromotionsList = usePerformNavigate(routes.list);

  const { Modal, openModalDialog } = useModalDialog({
    ...strings.publishError,
    onClose: goToPromotionsList,
  });

  useEffect(() => {
    if (
      ((selectedFilters.type_of_discount === TypeOfDiscount.FULL_MENU ||
        selectedFilters.type_of_discount === TypeOfDiscount.DOUBLE_DEAL) &&
        selectedFilters.discount_amount !== -1 &&
        selectedFilters.discount_target !== undefined) ||
      ((selectedFilters.type_of_discount === TypeOfDiscount.FREE_DELIVERY ||
        selectedFilters.type_of_discount === TypeOfDiscount.FREE_BASE_DELIVERY) &&
        selectedFilters.discount_target !== undefined)
    ) {
      if (!providerId) {
        return;
      }
      if (selectedFilters.is_bolt_plus) {
        setShouldShowSkeletons(false);
        setIsStatAvailable(false);
        setStatNotAvailableReason(StatNotAvailableReason.Bolt_Plus_Launch_Test);
        return;
      }

      const forecastRequest = {
        provider_id: providerId,
        filters: {
          [LABEL]: {
            type_of_discount: selectedFilters.type_of_discount,
            discount_target: selectedFilters.discount_target,
            ...((selectedFilters.type_of_discount === TypeOfDiscount.FULL_MENU ||
              selectedFilters.type_of_discount === TypeOfDiscount.DOUBLE_DEAL) && {
              discount_amount: selectedFilters.discount_amount,
            }),
          },
        },
      };

      forecastsFetcher.fetch(forecastRequest).then(({ result }) => {
        setIsStatAvailable(true);
        setShouldShowSkeletons(false);
        if (result[LABEL]) {
          setUpliftStats({
            weeklySpend: {
              currency: result[LABEL].currency,
              value: result[LABEL].expected_weekly_spend,
            },
            weeklyImpact: {
              currency: result[LABEL].currency,
              gmvValue: result[LABEL].expected_weekly_additional_gmv,
            },
            newUsers: Math.floor(result[LABEL].expected_weekly_new_users),
          });
        }
      });
    }
  }, [
    forecastsFetcher,
    providerId,
    selectedFilters,
    shouldShowSkeletons,
    isStatAvailable,
    statNotAvailableReason,
  ]);

  const itemLevelPromotionTag = useMemo(() => {
    return selectedMenuItems.length && selectedFilters.discount_amount
      ? `special_offer_${selectedFilters.discount_amount}`
      : undefined;
  }, [selectedFilters.discount_amount, selectedMenuItems.length]);

  const signIn = useCallback(() => {
    if (state.step === 2 && providerId) {
      signInFetcher
        .fetch({
          provider_id: providerId,
          trait_slug: state.promotion.trait,
          start_timestamp: toTimestamp(state.duration.start),
          end_timestamp: toTimestamp(state.duration.end),
          campaign_tag: itemLevelPromotionTag,
          menu_items_info: selectedMenuItems.length
            ? selectedMenuItems.map((item) => {
                return {
                  global_stable_context_id: item.globalStableContextId,
                  name: item.menuItemName,
                  product_id: item.productId,
                };
              })
            : undefined,
        })
        .then((promotion) => {
          analyticsFetcher
            .fetch({
              event: {
                type: AnalyticsEventName.PROMOTIONS_SIGN_UP,
                promotion_id: promotion.promotion_id,
              },
            })
            .finally(() => goToPromotionsList());
          if (selectedMenuItems.length) {
            mixpanelService.trackProviderEvent(
              PromotionsAnalyticsEvents.selected_items_promotion_created,
              {
                providerId,
              },
            );
          }
          if (
            selectedFinalChoice &&
            selectedFinalChoice.key === PromotionFilterKeys.DOUBLE_DEAL_VALUE
          ) {
            mixpanelService.trackProviderEvent(
              PromotionsAnalyticsEvents.double_deal_promotion_created,
              {
                providerId,
              },
            );
          } else if (
            selectedFinalChoice &&
            selectedFinalChoice.key !== PromotionFilterKeys.DOUBLE_DEAL_VALUE &&
            selectedDiscountTargeting === PromotionFilterKeys.DISCOUNT_TARGETING_BEST_CUSTOMERS
          ) {
            mixpanelService.trackProviderEvent(
              PromotionsAnalyticsEvents.best_user_promotion_created,
              {
                providerId,
              },
            );
          }
        })
        .catch((error) => {
          setErrorMessage(findError(strings.errorMessages, fetchCode(error)));
          openModalDialog();
        });
    }
  }, [
    goToPromotionsList,
    openModalDialog,
    providerId,
    signInFetcher,
    setErrorMessage,
    strings.errorMessages,
    state,
    analyticsFetcher,
    selectedMenuItems,
    itemLevelPromotionTag,
    mixpanelService,
    selectedFinalChoice,
    selectedDiscountTargeting,
  ]);

  const { stepZeroClickNext, stepOneClickNext, stepOneClickBack, stepTwoBack } = useMemo(
    () => ({
      stepZeroClickNext: (promotion: Promotion) => {
        setState((prev) => ({ ...prev, step: 1, promotion }));
      },
      stepOneClickNext: (duration: PromotionDuration) => {
        setState((previous) => ({
          ...(previous as AddPromotionStateOne),
          step: 2,
          duration,
        }));
      },
      stepOneClickBack: () => {
        setShouldShowSkeletons(true);
        setSelectedFilters(filterInitialState);
        setState((previous) => ({
          ...previous,
          step: 0,
        }));
      },
      stepTwoBack: () => {
        setState((previous) => ({
          ...(previous as AddPromotionStateTwo),
          step: 1,
        }));
      },
    }),
    [filterInitialState],
  );

  function getSelectedFilters(
    previousState: WeeklyForecastFiltersForUpliftStatsWithBoltPlus,
    choice: Choice,
  ): WeeklyForecastFiltersForUpliftStatsWithBoltPlus {
    let newState = { ...previousState };
    if (typeOfDiscountKeys.includes(choice.key)) {
      if (previousState.type_of_discount !== filterKeysToTypeMapping[choice.key]) {
        newState = { ...filterInitialState, type_of_discount: filterKeysToTypeMapping[choice.key] };
      } else {
        newState = { ...previousState, type_of_discount: filterKeysToTypeMapping[choice.key] };
      }
    } else if (discountTargetingKeys.includes(choice.key)) {
      setSelectedDiscountTargeting(choice.key);
      newState = {
        ...previousState,
        discount_target: filterKeysToTargetMapping[choice.key],
      };
    } else if (choice.key === PromotionFilterKeys.DISCOUNT_VALUE_PERCENTAGE) {
      newState = {
        ...previousState,
        discount_amount: Number(choice.value),
      };
    } else if (
      choice.key === PromotionFilterKeys.DOUBLE_DEAL_VALUE &&
      choice.type === CreatePromotionTemplateInputType.FINAL
    ) {
      newState = {
        ...previousState,
        discount_amount: Number(choice.double_deal_value.menu_percentage),
      };
    }
    if (choice.type === CreatePromotionTemplateInputType.FINAL) {
      setSelectedFinalChoice(choice);
      newState = {
        ...newState,
        is_bolt_plus: choice.is_bolt_plus,
      };
    }
    if (
      !(
        newState.type_of_discount === previousState.type_of_discount &&
        newState.discount_amount === previousState.discount_amount &&
        newState.discount_target === previousState.discount_target &&
        newState.is_bolt_plus === previousState.is_bolt_plus
      )
    ) {
      return newState;
    }
    return previousState;
  }

  function handleSetFilter(choice: Choice) {
    setSelectedFilters((prevState) => getSelectedFilters(prevState, choice));
  }

  const isStatAvailableHandler = useCallback((isAvailable: boolean) => {
    setIsStatAvailable(isAvailable);
  }, []);

  const selectedMenuItemNames = useMemo(() => {
    return selectedMenuItems
      .map((item) => {
        const selectedLocale = intl.locale;
        const fallBackLocale =
          'en-US' in item.menuItemName ? 'en-US' : Object.keys(item.menuItemName)[0];
        return item.menuItemName[selectedLocale]
          ? item.menuItemName[selectedLocale]
          : item.menuItemName[fallBackLocale];
      })
      .join(', ');
  }, [intl.locale, selectedMenuItems]);

  return (
    <>
      <PromotionCardFC
        strings={strings}
        onClose={goToPromotionsList}
        subtitle={<AddPromotionStepper currentStep={state.step} />}
        statsBoxProps={{
          upliftStats,
          showUpliftStatsSkeletons: shouldShowSkeletons,
          isStatAvailable,
          statNotAvailableReason,
        }}
      >
        {state.step === 0 ? (
          <SelectPromotionWizard
            providerId={providerId}
            onClickBack={goToPromotionsList}
            onClickNext={stepZeroClickNext}
            setFilter={handleSetFilter}
            setIsStatAvailable={isStatAvailableHandler}
            setSelectedMenuItems={setSelectedMenuItems}
            showMenuItemSelector={
              providerPromotionsPermissions?.can_view_selected_items_menu_editor ?? false
            }
            hideDescriptionForTypeOfDiscountSection={
              providerPromotionsPermissions.promotion_access_type ===
              PromotionAccessType.SELECTED_ITEMS
            }
          />
        ) : null}
        {state.step === 1 ? (
          <AddPromotionDuration
            initialValue={state.duration}
            promotion={state.promotion}
            onClickNext={stepOneClickNext}
            onClickBack={stepOneClickBack}
          />
        ) : null}
        {state.step === 2 ? (
          <AddPromotionOverview
            promotion={state.promotion}
            duration={state.duration}
            selectedMenuItemNames={selectedMenuItemNames}
            onClickPublish={signIn}
            onClickBack={stepTwoBack}
          />
        ) : null}
      </PromotionCardFC>
      <Modal text={errorMessage} />
    </>
  );
};
